import React, { useState, useRef, useEffect, useCallback } from "react";
import GoogleDrivePickerWebView from "./GoogleDrivePickerWebView";
import { API_BASE_URL } from "../../Common/Common";

const API_BASE = API_BASE_URL;
const DRIVE_FILES_ENDPOINT = "https://www.googleapis.com/drive/v3/files";
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const STORAGE_KEY = "gdrive_refresh_token";

function parseDriveTime(str) {
  if (!str) return null;
  const normalized = str.replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3");
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function isPublicFile(file) {
  // If permissions weren't returned by the API, we can't tell — treat as public.
  if (!file.permissions) return true;
  return file.permissions.some((p) => p.type === "anyone");
}

function mimeFilter(fileType) {
  if (fileType === "video") return `mimeType contains 'video/'`;
  if (fileType === "audio") return `mimeType contains 'audio/'`;
  if (fileType === "pdf") return `mimeType = 'application/pdf'`;
  return `mimeType contains 'image/'`;
}

async function folderShouldShow(folderId, accessToken, fileType) {
  try {
    const q = encodeURIComponent(
      `'${folderId}' in parents and ` +
        `(mimeType = 'application/vnd.google-apps.folder' or ${mimeFilter(fileType)}) and ` +
        `trashed=false`
    );
    const fields = encodeURIComponent("files(mimeType)");
    const url = `${DRIVE_FILES_ENDPOINT}?q=${q}&fields=${fields}&pageSize=1`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (!res.ok) return true;
    const data = await res.json();
    return (data.files || []).length > 0;
  } catch {
    return true;
  }
}

// Drive's list endpoint (loadFiles' fields= param) DOES request
// imageMediaMetadata, but it's known to be less reliable than a direct
// per-file GET for that same field — a file can genuinely have GPS EXIF
// that the list response omits. Same fix already applied to mobile's own
// Google Drive EXIF enrichment (MobileBottomNavBar.js): re-fetch metadata
// per-file right before use, only when the list response didn't already
// have it.
async function enrichWithMetadata(file, accessToken) {
  if (file.imageMediaMetadata?.location?.latitude != null) return file;
  if (!accessToken) return file;
  try {
    const res = await fetch(
      `${DRIVE_FILES_ENDPOINT}/${file.id}?fields=${encodeURIComponent("imageMediaMetadata(location(latitude,longitude),time)")}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!res.ok) return file;
    const data = await res.json();
    if (!data.imageMediaMetadata) return file;
    return { ...file, imageMediaMetadata: data.imageMediaMetadata };
  } catch {
    return file;
  }
}

function fileToImageItem(file) {
  const meta = file.imageMediaMetadata || {};
  const loc = meta.location || {};
  const date = parseDriveTime(meta.time);
  const lat = loc.latitude ?? null;
  const lon = loc.longitude ?? null;
  const thumb = file.thumbnailLink
    ? file.thumbnailLink.replace(/=s\d+(-[^?]*)?$/, "=s200")
    : null;
  return {
    url: `gdrive:${file.id}`,
    url_m: `gdrive:${file.id}`,
    thumb,
    name: file.name || "",
    exif_data:
      lat !== null && lon !== null && date !== null
        ? { latitude: lat, longitude: lon, date }
        : {},
  };
}


// Tries thumbnailLink first; if it fails (account-scoped URL), fetches via
// the Drive API with the Bearer token and renders as a blob URL.
function DriveImage({ file, token, style }) {
  const thumb = file.thumbnailLink
    ? file.thumbnailLink.replace(/=s\d+(-[^?]*)?$/, "=s220")
    : null;
  const [src, setSrc] = useState(thumb);
  const fetchedRef = useRef(false);

  const fetchBlob = useCallback(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetch(`https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => setSrc(URL.createObjectURL(blob)))
      .catch(() => setSrc(null));
  }, [file.id, token]);

  useEffect(() => {
    if (!thumb) fetchBlob();
  }, [thumb, fetchBlob]);

  if (!src)
    return (
      <span style={{ fontSize: "0.65rem", color: "#888", textAlign: "center", padding: 2 }}>
        No preview
      </span>
    );
  return <img src={src} onError={fetchBlob} style={style} alt={file.name} />;
}

const PICKER_MODE_KEY = "gdrive_picker_mode"; // "picker" | "folder"

export default function GoogleDrivePicker({ onSelect, multi = false, fileType = "image" }) {
  const [pickerMode, setPickerMode] = useState(
    () => localStorage.getItem(PICKER_MODE_KEY) || "picker"
  );

  const [token, setToken] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const [checkingFolders, setCheckingFolders] = useState(false);
  const [currentFolder, setCurrentFolder] = useState({ id: "root", name: "My Drive" });
  const [folderStack, setFolderStack] = useState([]);

  const pollRef = useRef(null);
  const pollStartRef = useRef(null);
  const tokenRef = useRef(null);

  const loadFiles = useCallback(async (accessToken, folderId, pageToken = null, reset = false) => {
    setLoading(true);
    try {
      const fid = folderId || "root";
      const q = encodeURIComponent(
        `'${fid}' in parents and ` +
          `(mimeType = 'application/vnd.google-apps.folder' or ${mimeFilter(fileType)}) and ` +
          `trashed=false`
      );
      const fields = encodeURIComponent(
        "nextPageToken,files(id,name,thumbnailLink,mimeType,imageMediaMetadata(location(latitude,longitude),time),permissions(type))"
      );
      let url =
        `${DRIVE_FILES_ENDPOINT}?q=${q}&fields=${fields}` +
        `&pageSize=50&orderBy=folder%2CmodifiedTime%20desc`;
      if (pageToken) url += `&pageToken=${encodeURIComponent(pageToken)}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const all = data.files || [];

      const mediaFiles = all.filter(
        (f) => f.mimeType !== "application/vnd.google-apps.folder"
      );
      setFiles((prev) => (reset ? mediaFiles : [...prev, ...mediaFiles]));
      setNextPageToken(data.nextPageToken || null);

      const folders = all.filter(
        (f) => f.mimeType === "application/vnd.google-apps.folder"
      );
      if (folders.length > 0) {
        setCheckingFolders(true);
        const results = await Promise.all(
          folders.map((f) =>
            folderShouldShow(f.id, accessToken, fileType).then((ok) => (ok ? f : null))
          )
        );
        const visible = results.filter(Boolean);
        if (visible.length > 0) {
          setFiles((prev) => {
            const media = prev.filter(
              (f) => f.mimeType !== "application/vnd.google-apps.folder"
            );
            return [...visible, ...media];
          });
        }
        setCheckingFolders(false);
      }
    } catch (e) {
      alert("Error loading images: " + e.message);
    } finally {
      setLoading(false);
    }
  }, [fileType]);

  useEffect(() => {
    const refreshToken = localStorage.getItem(STORAGE_KEY);
    if (!refreshToken) return;
    setLoading(true);
    fetch(`${API_BASE}/gdrive_refresh.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `refresh_token=${encodeURIComponent(refreshToken)}`,
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.access_token) {
          tokenRef.current = data.access_token;
          setToken(data.access_token);
          loadFiles(data.access_token, "root", null, true);
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setLoading(false);
        }
      })
      .catch(() => setLoading(false));

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loadFiles]);

  const startAuth = () => {
    const sessionId = crypto.randomUUID();
    window.open(`${API_BASE}/gdrive_auth.php?session=${sessionId}`, "_blank");
    setWaitingForAuth(true);
    pollStartRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        clearInterval(pollRef.current);
        setWaitingForAuth(false);
        alert("Sign-in timed out. Please try again.");
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/gdrive_poll.php?session=${sessionId}`);
        const data = await res.json();
        if (data.access_token) {
          clearInterval(pollRef.current);
          setWaitingForAuth(false);
          tokenRef.current = data.access_token;
          setToken(data.access_token);
          if (data.refresh_token) localStorage.setItem(STORAGE_KEY, data.refresh_token);
          loadFiles(data.access_token, "root", null, true);
        } else if (data.error) {
          clearInterval(pollRef.current);
          setWaitingForAuth(false);
          alert("Sign-in failed: " + data.error);
        }
      } catch {
        // keep polling on network hiccup
      }
    }, POLL_INTERVAL_MS);
  };

  const cancelAuth = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setWaitingForAuth(false);
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setFiles([]);
    setNextPageToken(null);
    setSelected(new Set());
    setFolderStack([]);
    setCurrentFolder({ id: "root", name: "My Drive" });
  };

  const navigateIntoFolder = (folder) => {
    setFolderStack((prev) => [...prev, currentFolder]);
    setCurrentFolder({ id: folder.id, name: folder.name });
    setSelected(new Set());
    loadFiles(token, folder.id, null, true);
  };

  const navigateBack = () => {
    const prev = folderStack[folderStack.length - 1];
    setFolderStack((s) => s.slice(0, -1));
    setCurrentFolder(prev);
    setSelected(new Set());
    loadFiles(token, prev.id, null, true);
  };

  const handleClick = async (file) => {
    const isFolder = file.mimeType === "application/vnd.google-apps.folder";
    if (isFolder) { navigateIntoFolder(file); return; }
    if (!multi) {
      const enriched = await enrichWithMetadata(file, tokenRef.current);
      // 2nd arg lets callers (e.g. Create Journal from Photos) fall back to
      // the folder name when no guide name was typed, without needing a
      // separate store read. Found 2026-09-24.
      onSelect([fileToImageItem(enriched)], currentFolder.name);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(file.id) ? next.delete(file.id) : next.add(file.id);
      return next;
    });
  };

  const handleConfirmMulti = async () => {
    const selectedFiles = files.filter((f) => selected.has(f.id));
    const enriched = await Promise.all(
      selectedFiles.map((f) => enrichWithMetadata(f, tokenRef.current))
    );
    const items = enriched.map(fileToImageItem);
    onSelect(items, currentFolder.name);
  };

  const selectableFiles = files.filter(
    (f) => f.mimeType !== "application/vnd.google-apps.folder" && isPublicFile(f)
  );
  const allSelected =
    selectableFiles.length > 0 && selectableFiles.every((f) => selected.has(f.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableFiles.map((f) => f.id)));
  };

  // ── render ──────────────────────────────────────────────────────────────────

  if (pickerMode !== "folder") {
    return <GoogleDrivePickerWebView onSelect={onSelect} multi={multi} fileType={fileType} />;
  }

  const containerStyle = {
    border: "1px solid #dee2e6",
    borderRadius: 6,
    overflow: "hidden",
    marginTop: 8,
  };

  const scrollStyle = {
    maxHeight: 320,
    overflowY: "auto",
  };

  const centeredStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
    gap: 12,
    textAlign: "center",
  };

  if (waitingForAuth) {
    return (
      <div style={containerStyle}>
        <div style={centeredStyle}>
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <strong>Complete sign-in</strong>
          <span style={{ fontSize: "0.85rem", color: "#666" }}>
            A new tab has opened. Sign in with Google, then return here — it will continue automatically.
          </span>
          <button className="btn btn-outline-secondary btn-sm" onClick={cancelAuth}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={centeredStyle}>
          {loading ? (
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          ) : (
            <>
              <strong>Google Drive</strong>
              <span style={{ fontSize: "0.85rem", color: "#666" }}>
                Sign in to browse {fileType === "video" ? "videos" : "images"} stored in your Google Drive.
              </span>
              <button className="btn btn-primary btn-sm" onClick={startAuth}>
                Sign in with Google
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Nav bar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "4px 8px", borderBottom: "1px solid #dee2e6", background: "#f8f9fa", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          {folderStack.length > 0 ? (
            <button className="btn btn-link btn-sm p-0" style={{ fontSize: "0.8rem" }} onClick={navigateBack}>
              ‹ {folderStack[folderStack.length - 1].name}
            </button>
          ) : null}
        </div>
        <span style={{ fontWeight: 600, fontSize: "0.85rem", flexShrink: 0 }}>
          {currentFolder.name}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button className="btn btn-link btn-sm p-0" style={{ fontSize: "0.8rem", color: "#888", whiteSpace: "nowrap" }}
            onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>

      {/* Folder checking */}
      {checkingFolders && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 8px",
          fontSize: "0.75rem", color: "#888", background: "#f8f9fa" }}>
          <div className="spinner-border spinner-border-sm" role="status" style={{ width: 10, height: 10, borderWidth: 1 }} />
          Checking folders…
        </div>
      )}

      {/* Info banner */}
      <div style={{ padding: "3px 8px", fontSize: "0.72rem", color: "#888",
        background: "#fff3cd", borderBottom: "1px solid #dee2e6" }}>
        ℹ️ Only {fileType === "video" ? "videos" : fileType === "audio" ? "audio files" : fileType === "pdf" ? "PDFs" : "images"} shared as "Anyone with the link" in Google Drive can be selected.
      </div>

      {/* Select all row — multi only */}
      {multi && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "3px 8px", borderBottom: "1px solid #dee2e6", background: "#f8f9fa" }}>
          <button className="btn btn-link btn-sm p-0" style={{ fontSize: "0.8rem" }} onClick={toggleSelectAll}>
            {allSelected ? "Deselect all" : "Select all"}
          </button>
          {selected.size > 0 && (
            <button className="btn btn-primary btn-sm" style={{ fontSize: "0.75rem" }}
              onClick={handleConfirmMulti}>
              Add {selected.size} {fileType === "video"
                ? (selected.size === 1 ? "video" : "videos")
                : (selected.size === 1 ? "photo" : "photos")}
            </button>
          )}
        </div>
      )}

      {/* File grid */}
      <div style={scrollStyle}>
        {loading && files.length === 0 ? (
          <div style={centeredStyle}>
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        ) : files.length === 0 ? (
          <div style={{ ...centeredStyle, padding: "16px" }}>
            <span style={{ fontSize: "0.85rem", color: "#888" }}>No {fileType === "video" ? "videos" : "images"} or folders found here.</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", padding: 4 }}>
            {files.map((file) => {
              const isFolder = file.mimeType === "application/vnd.google-apps.folder";
              const isSelected = selected.has(file.id);
              const isPublic = isPublicFile(file);
              return (
                <div
                  key={file.id}
                  onClick={() => handleClick(file)}
                  title={file.name}
                  style={{
                    width: 90, height: 90, margin: 3, cursor: "pointer",
                    borderRadius: 4, overflow: "hidden", position: "relative",
                    border: isSelected ? "3px solid #3598db" : "1px solid #dee2e6",
                    background: "#f0f0f0",
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    opacity: !isFolder && !isPublic ? 0.5 : 1,
                  }}
                >
                  {isFolder ? (
                    <>
                      <span style={{ fontSize: 32 }}>📁</span>
                      <span style={{ fontSize: "0.6rem", textAlign: "center", padding: "0 2px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: "100%", color: "#333" }}>
                        {file.name}
                      </span>
                    </>
                  ) : fileType === "video" ? (
                    <>
                      <span style={{ fontSize: 32 }}>🎬</span>
                      <span style={{ fontSize: "0.6rem", textAlign: "center", padding: "0 4px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: "100%", color: "#333" }}>
                        {file.name}
                      </span>
                    </>
                  ) : fileType === "audio" ? (
                    <>
                      <span style={{ fontSize: 32 }}>🎵</span>
                      <span style={{ fontSize: "0.6rem", textAlign: "center", padding: "0 4px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: "100%", color: "#333" }}>
                        {file.name}
                      </span>
                    </>
                  ) : fileType === "pdf" ? (
                    <>
                      <span style={{ fontSize: 32 }}>📄</span>
                      <span style={{ fontSize: "0.6rem", textAlign: "center", padding: "0 4px",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        maxWidth: "100%", color: "#333" }}>
                        {file.name}
                      </span>
                    </>
                  ) : (
                    <DriveImage
                      file={file}
                      token={token}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}
                  {isSelected && (
                    <div style={{ position: "absolute", top: 2, right: 2,
                      background: "#3598db", borderRadius: "50%",
                      width: 18, height: 18, display: "flex", alignItems: "center",
                      justifyContent: "center", color: "#fff", fontSize: 11 }}>
                      ✓
                    </div>
                  )}
                  {!isFolder && !isPublic && (
                    <div style={{ position: "absolute", top: 2, left: 2,
                      background: "rgba(0,0,0,0.5)", borderRadius: 3,
                      padding: "1px 3px", fontSize: 10, color: "#fff" }}>
                      🔒
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {nextPageToken && !loading && (
          <div style={{ textAlign: "center", padding: "8px" }}>
            <button className="btn btn-outline-secondary btn-sm"
              onClick={() => loadFiles(token, currentFolder.id, nextPageToken)}>
              Load more
            </button>
          </div>
        )}
        {loading && files.length > 0 && (
          <div style={{ textAlign: "center", padding: 8 }}>
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          </div>
        )}
      </div>
    </div>
  );
}
